import React, { useState } from 'react';
import { Outlet } from 'react-router-dom';
import { Box, Drawer, useMediaQuery, useTheme } from '@mui/material';

import { ViewModeProvider } from '../contexts/ViewModeContext';
import { AppHeader } from './layout/AppHeader';
import { AppSidebar } from './layout/AppSidebar';
import { MobileDrawer } from './layout/MobileDrawer';
import { useNavigate } from 'react-router-dom';

const LayoutContent: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [mobileMenuAnchor, setMobileMenuAnchor] = useState<null | HTMLElement>(null);

  const drawerWidth = sidebarCollapsed ? 70 : 250;

  const handleDrawerToggle = () => setMobileOpen((prev) => !prev);

  const handleMobileMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setMobileMenuAnchor(event.currentTarget);
  };

  const handleMobileMenuClose = () => setMobileMenuAnchor(null);

  const handleNavigation = (path: string) => {
    navigate(path);
    setMobileOpen(false);
    handleMobileMenuClose();
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      {/* Top AppBar */}
      <AppHeader
        sidebarCollapsed={sidebarCollapsed}
        setSidebarCollapsed={setSidebarCollapsed}
        onMobileMenuOpen={handleMobileMenuOpen}
      />

      {/* Desktop permanent sidebar */}
      <Box component="nav" sx={{ width: { md: drawerWidth }, flexShrink: { md: 0 } }}>
        <Drawer
          variant={isMobile ? 'temporary' : 'permanent'}
          open={isMobile ? mobileOpen : true}
          onClose={handleDrawerToggle}
          ModalProps={{ keepMounted: true }}
          sx={{
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: drawerWidth,
              backgroundColor: 'primary.main',
              borderRight: 'none',
              display: { xs: 'none', md: 'block' },
              transition: 'width 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
              overflowX: 'hidden',
            },
          }}
        >
          <AppSidebar
            sidebarCollapsed={sidebarCollapsed}
            setSidebarCollapsed={setSidebarCollapsed}
            onDrawerToggle={handleDrawerToggle}
          />
        </Drawer>
      </Box>

      {/* Mobile hamburger menu */}
      {isMobile && (
        <MobileDrawer
          anchorEl={mobileMenuAnchor}
          onClose={handleMobileMenuClose}
          onNavigate={handleNavigation}
        />
      )}

      {/* Main content */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          mt: '64px',
          ml: { xs: 0, md: `${drawerWidth}px` },
          backgroundColor: 'background.default',
          minHeight: 'calc(100vh - 64px)',
          transition: 'margin-left 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
          overflowX: 'hidden',
          width: { xs: '100%', md: `calc(100% - ${drawerWidth}px)` },
        }}
      >
        <Outlet />
      </Box>
    </Box>
  );
};

const Layout: React.FC = () => {
  return (
    <ViewModeProvider>
      <LayoutContent />
    </ViewModeProvider>
  );
};

export default Layout;
